import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';

export interface TopbarNotificationItem {
  id: string;
  title: string;
  message: string;
  deepLink: string;
  createdAt: string;
  isRead: boolean;
}

interface NotificationToastEntry extends TopbarNotificationItem {
  toastId: number;
}

@Component({
  selector: 'marina-topbar-notifications',
  templateUrl: './topbar-notifications.component.html',
  styleUrl: './topbar-notifications.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopbarNotificationsComponent implements OnInit, OnChanges, OnDestroy {
  private static nextToastId = 1;
  private readonly toastTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private lastIncomingToastId: string | null = null;
  private relativeClockTimer?: ReturnType<typeof setInterval>;
  /** Precomputed labels so template binding never recalculates mid change-detection (NG0100). */
  private timeLabels = new Map<string, string>();

  @ViewChild(MatMenuTrigger) private menuTrigger?: MatMenuTrigger;

  @Input() items: TopbarNotificationItem[] = [];
  @Input() unreadCount = 0;
  @Input() loading = false;
  /** When true, live pushes shown as top-right toasts. */
  @Input() enableToasts = false;
  /** Latest live notification to toast (from toast$). */
  @Input() incomingToast: TopbarNotificationItem | null = null;
  /** Auto-dismiss duration in ms. */
  @Input() toastDurationMs = 5000;

  @Output() openMenu = new EventEmitter<void>();
  @Output() itemClick = new EventEmitter<TopbarNotificationItem>();
  @Output() markAllRead = new EventEmitter<void>();
  @Output() toastClick = new EventEmitter<TopbarNotificationItem>();

  toasts: NotificationToastEntry[] = [];

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone
  ) {}

  get badgeHidden(): boolean {
    return !this.unreadCount || this.unreadCount <= 0;
  }

  get badgeText(): string {
    return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
  }

  ngOnInit(): void {
    this.rebuildTimeLabels();
    this.ngZone.runOutsideAngular(() => {
      this.relativeClockTimer = setInterval(() => {
        this.ngZone.run(() => {
          this.rebuildTimeLabels();
          this.cdr.markForCheck();
        });
      }, 30_000);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.rebuildTimeLabels();
    }

    if (!changes['incomingToast'] || !this.enableToasts || !this.incomingToast) {
      return;
    }
    if (this.lastIncomingToastId === this.incomingToast.id) {
      return;
    }
    this.lastIncomingToastId = this.incomingToast.id;
    const toast = this.incomingToast;
    // Defer past the parent CD cycle — mutating toasts in ngOnChanges causes NG0100.
    queueMicrotask(() => {
      this.enqueueToast(toast);
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    if (this.relativeClockTimer) {
      clearInterval(this.relativeClockTimer);
      this.relativeClockTimer = undefined;
    }
    for (const timer of this.toastTimers.values()) {
      clearTimeout(timer);
    }
    this.toastTimers.clear();
  }

  openNotificationsMenu(): void {
    this.menuTrigger?.openMenu();
  }

  onMenuOpened(): void {
    this.rebuildTimeLabels();
    this.openMenu.emit();
  }

  onItemClick(item: TopbarNotificationItem, event: Event): void {
    event.stopPropagation();
    this.itemClick.emit(item);
    this.menuTrigger?.closeMenu();
  }

  onMarkAllRead(event: Event): void {
    event.stopPropagation();
    this.markAllRead.emit();
  }

  onToastBodyClick(toast: NotificationToastEntry, event: Event): void {
    event.stopPropagation();
    this.dismissToast(toast.toastId);
    this.toastClick.emit(toast);
  }

  onToastCloseClick(toast: NotificationToastEntry, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.dismissToast(toast.toastId);
  }

  /** Stable lookup — never compute relative time during template evaluation. */
  timeLabel(item: TopbarNotificationItem): string {
    return this.timeLabels.get(item.id) ?? '';
  }

  private rebuildTimeLabels(): void {
    const nowMs = Date.now();
    const next = new Map<string, string>();
    for (const item of this.items) {
      next.set(item.id, this.computeRelativeLabel(item.createdAt, nowMs));
    }
    this.timeLabels = next;
  }

  private computeRelativeLabel(iso: string, nowMs: number): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const diffMs = nowMs - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  }

  private enqueueToast(item: TopbarNotificationItem): void {
    if (this.toasts.some((t) => t.id === item.id)) return;

    const toastId = TopbarNotificationsComponent.nextToastId++;
    const entry: NotificationToastEntry = { ...item, toastId };
    this.toasts = [entry, ...this.toasts].slice(0, 3);

    const timer = setTimeout(() => this.dismissToast(toastId), this.toastDurationMs);
    this.toastTimers.set(toastId, timer);
  }

  private dismissToast(toastId: number): void {
    const timer = this.toastTimers.get(toastId);
    if (timer) {
      clearTimeout(timer);
      this.toastTimers.delete(toastId);
    }
    this.toasts = this.toasts.filter((t) => t.toastId !== toastId);
    this.cdr.markForCheck();
  }
}
